/**
 * Ready-made data for the question, card and colour-sort blocks, taken from the AI Essentials
 * lessons they were built for. The lesson builder uses them as the starting point for a new block.
 */

/** Lesson 3, "How did it learn to write?". */
export const QUICK_QUESTION_EXAMPLE = {
  question: "ChatGPT writes fluent emails, essays and code. How do you think it learned to do that?",
  options: [
    { id: "a", label: "Programmers wrote grammar rules for it", feedback: "Not quite. Nobody wrote grammar rules for it. Language is far too messy for rules to capture." },
    { id: "b", label: "It read huge amounts of text and learned the patterns", feedback: "Exactly right." },
    { id: "c", label: "It looks up every answer on the internet", feedback: "Not quite. Some assistants can search the web as an extra tool, but the model itself doesn't look things up." },
    { id: "d", label: "I'm not sure", feedback: "That's fine. That's what this lesson is for." },
  ],
  correct_id: "b",
  explanation:
    "It was trained on enormous amounts of text and learned the patterns of language: which words follow which, how arguments are structured, what an email sounds like. In this lesson, you'll see how that works by training a model yourself.",
};

/** Lesson 4, "Why AI makes things up": three reasons LLMs hallucinate. */
export const CARD_GRID_EXAMPLE = {
  columns: 3,
  cards: [
    { id: "c1", icon: "🎲", title: "It predicts, it doesn't look up", body: "An LLM has no built-in fact database. It generates text that sounds right, one likely token at a time." },
    { id: "c2", icon: "🕳️", title: "Gaps in its training data", body: "When it knows little about a topic, or the event happened after its knowledge cutoff, it fills the gap with plausible guesses." },
    { id: "c3", icon: "🙂", title: "Built to sound helpful", body: "It's trained to give fluent, confident answers. Sounding sure is not the same as being right." },
  ],
  caption: "Three reasons LLMs hallucinate",
};

const looks = (text: string) => ({ label: "What it looks like", text, color: "amber" as const });
const move = (text: string) => ({ label: "Your move", text, color: "emerald" as const });

/** Lesson 10, "Seven risks to know". */
export const TILE_REVEAL_EXAMPLE = {
  prompt: "Responsible AI isn't about being scared of AI. It's about knowing where it can go wrong, and having a move ready for each. Tap each risk.",
  tiles: [
    { id: "r1", icon: "🎲", title: "Hallucination", boxes: [looks("Confident answers that are wrong: invented facts, fake sources, made-up quotes."), move("Match checking to the stakes, and run important outputs around the Verification Wheel.")] },
    { id: "r2", icon: "⚖️", title: "Bias", boxes: [looks("Stereotypes in text and images, unfair assumptions about gender, age, religion or nationality, and one-sided answers."), move("Ask \"who could this exclude?\" Request balanced perspectives, and review anything that affects people.")] },
    { id: "r3", icon: "🔏", title: "Privacy", boxes: [looks("Pasting personal data (names, numbers, health, money) into tools that may store it."), move("Use the Data Traffic Light. Replace details with placeholders, and check your tool's data settings.")] },
    { id: "r4", icon: "🛡️", title: "Security", boxes: [looks("Convincing AI-written phishing, fake AI apps and browser extensions, and company secrets leaking through AI tools."), move("Use trusted, approved tools only. Never share passwords or codes, and verify unexpected requests.")] },
    { id: "r5", icon: "©️", title: "Copyright", boxes: [looks("Copying others' work through AI, or using AI images or text commercially without checking the terms."), move("Don't paste paid content to reproduce it, credit sources, and check your tool's terms before commercial use.")] },
    { id: "r6", icon: "📢", title: "Deepfakes and misinformation", boxes: [looks("Cloned voices, fake videos of real people, and fake \"breaking news\" images spreading on WhatsApp."), move("Pause before you share or pay. Verify through a trusted source or a different channel.")] },
    { id: "r7", icon: "🧠", title: "Over-reliance", boxes: [looks("Letting AI make decisions it shouldn't, losing your own skills, or submitting AI work you don't understand."), move("Keep a human in charge of important decisions, understand what you submit, and disclose AI use when required.")] },
  ],
};

/** Lesson 10, "The Data Traffic Light". */
export const COLOR_SORT_EXAMPLE = {
  prompt: "Before you paste anything into an AI tool, run it through the Traffic Light. Sort each item.",
  categories: [
    { id: "green", label: "Green", color: "emerald" as const, icon: "🟢" },
    { id: "amber", label: "Amber", color: "amber" as const, icon: "🟠" },
    { id: "red", label: "Red", color: "rose" as const, icon: "🔴" },
  ],
  items: [
    { id: "i1", text: "A public news article you want summarised", category_id: "green", why: "Public information. Go ahead." },
    { id: "i2", text: "Your own essay draft, with no personal details", category_id: "green", why: "Your own non-sensitive writing is fine." },
    { id: "i3", text: "A made-up example you wrote to test a prompt", category_id: "green", why: "Invented data is always safe." },
    { id: "i4", text: "Meeting notes that mention client names", category_id: "amber", why: "Replace client names with placeholders, or use an approved company tool." },
    { id: "i5", text: "A customer complaint email", category_id: "amber", why: "Remove the customer's name, contact details and order number first." },
    { id: "i6", text: "Survey results that include respondents' names", category_id: "amber", why: "Strip the names. The AI only needs the answers." },
    { id: "i7", text: "Your company's unreleased product plans", category_id: "amber", why: "Confidential: only use a tool your company has approved for this." },
    { id: "i8", text: "Your password or a one-time code", category_id: "red", why: "Never. No legitimate AI task needs these." },
    { id: "i9", text: "A CNIC, Iqama or passport number", category_id: "red", why: "Never share government ID numbers with AI tools." },
    { id: "i10", text: "A patient's medical report with their name", category_id: "red", why: "Health data about a named person is highly sensitive and often legally protected." },
  ],
  points_per_item: 2,
};

/** Lesson 1, "What AI can and can't do". */
export const COMPARE_COLUMNS_EXAMPLE = {
  columns: [
    {
      id: "good",
      title: "AI is good at",
      icon: "✅",
      color: "emerald" as const,
      body: "- Spotting patterns in huge amounts of data\n- Predicting what's likely to happen next\n- Generating text, images and code quickly\n- Translating and summarising\n- Working at a speed and scale no human can\n- Doing the same task consistently, without tiring",
    },
    {
      id: "hard",
      title: "AI struggles with",
      icon: "⚠️",
      color: "amber" as const,
      body: "- Guaranteeing that what it says is true\n- Knowing about events after it was trained\n- Common sense in unfamiliar situations\n- Understanding meaning the way people do\n- Being free of bias, since it learns from human data\n- Taking responsibility for decisions",
    },
  ],
  caption: "Strengths and limits of today's AI",
};

const LEVELS = ["AI decides alone", "AI decides, human spot-checks", "AI suggests, human decides", "Human only"];

/** Lesson 10, "Should AI decide?". */
export const SLIDER_JUDGE_EXAMPLE = {
  prompt: "Set the right level for each decision, then lock it in.",
  mode: "judge" as const,
  levels: LEVELS,
  start: 1,
  left_label: "← More AI",
  right_label: "More human →",
  items: [
    { id: "d1", text: "Filtering spam from your email inbox", accept: [0, 1], why: "Low stakes and easy to fix. You can always check the spam folder." },
    { id: "d2", text: "Recommending the next song in a playlist", accept: [0], why: "Nothing bad happens if it picks a song you skip." },
    { id: "d3", text: "Flagging suspicious card transactions for review", accept: [1, 2], why: "AI is great at spotting patterns. People should confirm before blocking a customer's money for long." },
    { id: "d4", text: "Shortlisting job applicants from 500 CVs", accept: [2], why: "It affects livelihoods, and AI can copy past hiring bias. People must review, decide and be able to explain why." },
    { id: "d5", text: "Reading X-rays to flag possible fractures", accept: [2], why: "AI can be a powerful second pair of eyes, but a qualified doctor makes the diagnosis." },
    { id: "d6", text: "Deciding a student's final exam grade", accept: [2, 3], why: "Grades shape futures. A teacher must decide, and students must be able to question it." },
    { id: "d7", text: "Deciding whether to fire an employee", accept: [3], why: "A life-changing decision about a person needs human judgement, context and accountability." },
  ],
};

/** Lesson 21, "Your AI competencies". */
export const SLIDER_RATE_EXAMPLE = {
  prompt: "The course was built around six competencies. Rate yourself honestly today, from 1 (just starting) to 4 (confident enough to teach someone else).",
  mode: "rate" as const,
  levels: ["1", "2", "3", "4"],
  start: 1,
  items: [
    { id: "lit", text: "AI literacy", hint: "Explain modern AI concepts" },
    { id: "int", text: "AI interaction", hint: "Communicate effectively with AI" },
    { id: "app", text: "AI application", hint: "Use AI for real-world tasks" },
    { id: "ps", text: "AI problem solving", hint: "Identify meaningful AI opportunities" },
    { id: "wf", text: "AI workflows", hint: "Design simple AI-assisted workflows" },
    { id: "resp", text: "Responsible AI", hint: "Evaluate risks and AI outputs" },
  ],
  summary_note: "Put it into your 30-day plan from Lesson 20.",
};

/** Lesson 5, "Project: your AI Capability Card": a one-entry form with a live card. */
export const CAPABILITY_CARD_EXAMPLE = {
  prompt: "Turn your Test Drive notes from Lessons 2, 3 and 4 into a one-page Capability Card.",
  min_words: 20,
  fields: [
    { id: "assistant", label: "Assistant you tested", options: ["ChatGPT", "Claude", "Gemini", "Another"] },
    { id: "name", label: "Your name", hint: "Shown on the card", optional: true },
    { id: "pred", label: "Your Lesson 1 prediction", hint: "\"I think it will be great at ___ and bad at ___\"", long: true },
    { id: "right", label: "Were you right?", hint: "What surprised you?", long: true },
    { id: "g0", label: "Strength 1" },
    { id: "g1", label: "Strength 2" },
    { id: "g2", label: "Strength 3" },
    { id: "b0", label: "Weakness 1" },
    { id: "b1", label: "Weakness 2" },
    { id: "b2", label: "Weakness 3" },
    { id: "r0", label: "Rule 1", placeholder: "e.g. Always check names, numbers and sources" },
    { id: "r1", label: "Rule 2" },
    { id: "r2", label: "Rule 3" },
    { id: "catch", label: "My best hallucination catch", hint: "One or two sentences", long: true },
  ],
  preview: {
    heading: "AI Capability Card",
    title_field: "assistant",
    title_fallback: "Your assistant",
    sections: [
      { label: "✅ Does well", fields: ["g0", "g1", "g2"], style: "list" as const },
      { label: "⚠️ Does badly", fields: ["b0", "b1", "b2"], style: "list" as const },
      { label: "🛡️ My rules for using it safely", fields: ["r0", "r1", "r2"], style: "list" as const },
      { label: "I predicted / What I found", fields: ["pred", "right"], style: "text" as const },
      { label: "🔎 Best hallucination catch", fields: ["catch"], style: "text" as const },
    ],
    footer_field: "name",
    footer: "AI Essentials, Week 1 on inaraX",
    download_name: "ai-capability-card",
  },
};

/** The Prompt Playbook wrap-up: everything the learner wrote for the project. */
export const PROJECT_EXPORT_EXAMPLE = {
  title: "Take your Playbook with you",
  intro: "Download everything you've written so far as a PDF, with each question and your answers.",
  filename: "my-prompt-playbook",
};

/** Lesson 20, "The future of work with AI": jobs are bundles of tasks. */
/** Spot the errors: the AI Essentials Lesson 9 lab (a brief, and an AI-drafted email with 8 problems). */
export const SPOT_THE_ERRORS_EXAMPLE = {
  prompt:
    "An events team asked AI to turn their internal brief into an invitation email for school principals. The email has **8 problems**, including something important that's missing. Your job: **find, verify, correct and explain**.\n\nCompare the email with the brief. Tap any sentence you think is wrong, then pick which part of the Wheel it fails and write a quick correction.",
  source_title: "Internal brief (the truth)",
  source_icon: "📄",
  source:
    "- **Event:** Karachi Youth Tech Summit 2026\n- **Date:** Saturday 14 November, 9:30am–5pm\n- **Venue:** Expo Centre, Hall 2, Karachi\n- **Who:** students aged 16–24, all genders welcome\n- **Seats:** 200, free, first come first served\n- **Registration closes:** 5 November\n- **Speakers:** 6 confirmed\n- **Lunch** provided; bring student ID\n- **Questions:** events@summit.example\n- **Internal only:** venue manager Imran, 0300-1234567. Do not share.",
  document_title: "AI-drafted email to principals",
  document_icon: "✉️",
  categories: [
    { id: "acc", label: "Accuracy", color: "indigo" },
    { id: "rel", label: "Relevance", color: "emerald" },
    { id: "com", label: "Completeness", color: "amber" },
    { id: "src", label: "Source quality", color: "violet" },
    { id: "rea", label: "Reasoning", color: "sky" },
    { id: "bias", label: "Bias", color: "rose" },
    { id: "safe", label: "Safety", color: "orange" },
  ],
  sentences: [
    { id: "s1", text: "Dear Principals, we're excited to invite your students to the Karachi Youth Tech Summit 2026!" },
    {
      id: "s2",
      text: "The summit takes place on Sunday 14 November at the Expo Centre, Hall 2.",
      category_id: "acc",
      why: "The brief says Saturday, not Sunday.",
      fix: "The summit takes place on Saturday 14 November, 9:30am–5pm, at the Expo Centre, Hall 2.",
    },
    {
      id: "s3",
      text: "Karachi is Pakistan's largest city and a major port on the Arabian Sea.",
      category_id: "rel",
      why: "True, but irrelevant. Principals don't need a geography lesson.",
      fix: "(Delete this sentence.)",
    },
    {
      id: "s4",
      text: "It's a free event, so there's no limit on how many students can attend.",
      category_id: "rea",
      why: "Free doesn't mean unlimited: there are only 200 seats, first come first served.",
      fix: "The event is free, but places are limited to 200 students, first come first served.",
    },
    {
      id: "s5",
      text: "The day will feature 10 confirmed speakers from leading tech companies.",
      category_id: "acc",
      why: "The brief says 6 confirmed speakers, and doesn't say where they're from.",
      fix: "The day will feature 6 confirmed speakers.",
    },
    {
      id: "s6",
      text: "According to a 2024 UNESCO report, 90% of students who attend tech summits find jobs within a year.",
      category_id: "src",
      why: "This statistic isn't in the brief, and there's no evidence the report exists. It's very likely invented.",
      fix: "(Delete this sentence, unless you can find and cite the real source.)",
    },
    {
      id: "s7",
      text: "The summit is ideal for boys aged 16–24 who are interested in engineering.",
      category_id: "bias",
      why: "The brief says all genders are welcome. This excludes girls, and narrows it to engineering.",
      fix: "The summit is open to all students aged 16–24 who are curious about technology.",
    },
    { id: "s8", text: "Lunch is provided, and students should bring their student ID." },
    {
      id: "s9",
      text: "For any questions, call our venue manager Imran directly on 0300-1234567.",
      category_id: "safe",
      why: "This shares a private, internal-only phone number. That's a privacy breach.",
      fix: "For any questions, email events@summit.example.",
    },
    { id: "s10", text: "We look forward to seeing your students there!" },
  ],
  missing: {
    question: "Is anything important missing from the email? Select any that apply.",
    options: [
      { id: "m1", label: "Registration deadline (5 November)", correct: true },
      { id: "m2", label: "The venue", correct: false },
      { id: "m3", label: "Lunch details", correct: false },
      { id: "m4", label: "Age range", correct: false },
    ],
    category_id: "com",
    why: "The registration deadline (5 November) and first-come-first-served rule are missing. Principals need them to act.",
  },
  allow_notes: true,
  corrected_title: "The corrected email",
  corrected:
    "Dear Principals, we're excited to invite your students to the Karachi Youth Tech Summit 2026!\n\nThe summit takes place on Saturday 14 November, 9:30am–5pm, at the Expo Centre, Hall 2, Karachi. It's open to all students aged 16–24 who are curious about technology, and it's free, but places are limited to 200 students, first come first served. Registration closes on 5 November.\n\nThe day will feature 6 confirmed speakers. Lunch is provided, and students should bring their student ID.\n\nFor any questions, email events@summit.example. We look forward to seeing your students there!",
};

export const SCENARIO_SORT_EXAMPLE = {
  prompt: "AI doesn't replace jobs in one go. It changes individual tasks. Pick a job and sort each of its tasks: will AI mostly **automate** it, **assist** a person with it, or will it stay **mostly human**? Then compare with an expert view.",
  categories: [
    { id: "automate", label: "Automate", color: "sky" as const, icon: "⚙️", summary: "tasks mostly automated" },
    { id: "assist", label: "AI assists", color: "violet" as const, icon: "🤝", summary: "tasks AI assists with" },
    { id: "human", label: "Mostly human", color: "emerald" as const, icon: "👤", summary: "tasks that stay human" },
  ],
  scenarios: [
    {
      id: "marketing",
      label: "Marketing executive",
      icon: "📣",
      items: [
        { id: "marketing-1", text: "Resize images for different platforms", category_id: "automate", why: "Repetitive and rule-based. Tools already do it." },
        { id: "marketing-2", text: "Write first drafts of social posts", category_id: "assist", why: "AI drafts quickly; you add brand voice, real details and judgement." },
        { id: "marketing-3", text: "Analyse campaign performance data", category_id: "assist", why: "AI finds patterns fast; you decide what they mean and what to do." },
        { id: "marketing-4", text: "Schedule posts across channels", category_id: "automate", why: "Scheduling tools and automations handle it." },
        { id: "marketing-5", text: "Decide the campaign strategy", category_id: "human", why: "AI can offer ideas, but strategy needs judgement and accountability." },
        { id: "marketing-6", text: "Build relationships with clients and partners", category_id: "human", why: "Trust is built person to person." },
      ],
      closing: "The marketing executive doesn't disappear. The job shifts towards the human tasks, with AI handling or speeding up the rest.",
    },
    {
      id: "teacher",
      label: "Teacher",
      icon: "🧑‍🏫",
      items: [
        { id: "teacher-1", text: "Mark multiple-choice tests", category_id: "automate", why: "Fully rule-based, and easy to check." },
        { id: "teacher-2", text: "Create quiz questions", category_id: "assist", why: "AI drafts questions; you check accuracy and level." },
        { id: "teacher-3", text: "Plan lessons", category_id: "assist", why: "AI suggests activities and resources; you shape them for your class." },
        { id: "teacher-4", text: "Give feedback on essays", category_id: "assist", why: "AI can suggest comments; the teacher decides the grade and tone." },
        { id: "teacher-5", text: "Record attendance", category_id: "automate", why: "Simple data entry, easily automated." },
        { id: "teacher-6", text: "Support a struggling student", category_id: "human", why: "Care, trust and knowing the child are human." },
      ],
      closing: "The teacher doesn't disappear. The job shifts towards the human tasks, with AI handling or speeding up the rest.",
    },
    {
      id: "accountant",
      label: "Accountant",
      icon: "📊",
      items: [
        { id: "accountant-1", text: "Enter invoice data into the system", category_id: "automate", why: "AI can read invoices and fill fields, with exceptions checked." },
        { id: "accountant-2", text: "Reconcile bank statements", category_id: "automate", why: "Matching transactions is rule-based and increasingly automated." },
        { id: "accountant-3", text: "Spot unusual transactions", category_id: "assist", why: "AI flags patterns; an accountant investigates." },
        { id: "accountant-4", text: "Draft monthly reports", category_id: "assist", why: "AI drafts; the accountant verifies every figure." },
        { id: "accountant-5", text: "Advise a client on tax planning", category_id: "human", why: "Regulated, high-stakes advice needs a qualified, accountable person." },
        { id: "accountant-6", text: "Explain results to a nervous business owner", category_id: "human", why: "Clear, empathetic explanation builds trust." },
      ],
      closing: "The accountant doesn't disappear. The job shifts towards the human tasks, with AI handling or speeding up the rest.",
    },
    {
      id: "support",
      label: "Customer support agent",
      icon: "🎧",
      items: [
        { id: "support-1", text: "Sort incoming tickets by topic", category_id: "automate", why: "Classification is an AI strength, as you saw in Lesson 19." },
        { id: "support-2", text: "Answer common FAQs", category_id: "automate", why: "An assistant grounded in the FAQ can handle these, with spot-checks." },
        { id: "support-3", text: "Draft replies to complex issues", category_id: "assist", why: "AI drafts; the agent checks facts and tone." },
        { id: "support-4", text: "Summarise call notes", category_id: "assist", why: "AI summarises; the agent confirms nothing important is missing." },
        { id: "support-5", text: "Calm an angry customer on the phone", category_id: "human", why: "Empathy and real-time judgement stay human." },
        { id: "support-6", text: "Decide on refunds and exceptions", category_id: "human", why: "Money and fairness decisions need human accountability." },
      ],
      closing: "The customer support agent doesn't disappear. The job shifts towards the human tasks, with AI handling or speeding up the rest.",
    },
  ],
  footnote: "These are informed judgements about likely change over the next few years, not predictions. Different workplaces will adopt AI at very different speeds.",
};

/** Lesson 16, "The AI problem-solving framework": is AI the right tool? */
export const DECISION_TREE_EXAMPLE = {
  prompt: "Think of a problem from your study, work or home. Answer five honest questions about it, and the decision tree will tell you what to do. Try it with two different problems.",
  questions: [
    { id: "q1", text: "Is this task repeated often, or does it involve a lot of information?", hint: "One-off tasks are often quicker to just do yourself.", exit: { on: "no" as const, outcome_id: "one-off" } },
    { id: "q2", text: "Would a simpler fix work: a checklist, a template, a reminder, or a conversation?", hint: "Be honest. Simple fixes are cheaper, faster and safer.", exit: { on: "yes" as const, outcome_id: "simple-fix" } },
    { id: "q3", text: "Does it involve text, images, data or patterns: writing, summarising, sorting, answering, analysing?", hint: "These are the things AI is good at.", exit: { on: "no" as const, outcome_id: "not-ai" } },
    { id: "q4", text: "If AI gets it wrong, can a person catch and fix the mistake before it causes harm?", hint: "Think about health, money, safety and people's rights.", exit: { on: "no" as const, outcome_id: "high-risk" } },
    { id: "q5", text: "Does it need sensitive personal data: health, finances, ID numbers, private details?", hint: "Remember the Data Traffic Light.", exit: { on: "yes" as const, outcome_id: "safeguards" } },
  ],
  outcomes: [
    { id: "one-off", tone: "caution" as const, title: "Probably not worth it", body: "For a one-off task, setting up AI may take longer than doing it yourself. You can still use a chat assistant for a quick first draft or idea." },
    { id: "simple-fix", tone: "caution" as const, title: "Try the simple fix first", body: "Start with the non-AI fix. If the problem is still there after a few weeks, come back and consider AI." },
    { id: "not-ai", tone: "stop" as const, title: "Not an AI problem", body: "This sounds like a physical, technical or people problem. AI might help you research solutions, but it won't solve it directly." },
    { id: "high-risk", tone: "stop" as const, title: "High risk: keep humans in charge", body: "Use AI only as an assistant that suggests, with a qualified person making every decision, or don't use AI here at all." },
    { id: "safeguards", tone: "caution" as const, title: "Possible, with strong safeguards", body: "Only use approved tools, remove or replace personal details, and keep a human reviewing every output. Check your organisation's policy first." },
    { id: "good", tone: "good" as const, title: "A good AI opportunity", body: "Start small, measure the before and after, and keep a person reviewing the outputs. This could make a great capstone." },
  ],
  final_outcome_id: "good",
};

/** Lesson 13, "AI for visual content": the visual prompt builder. */
export const PROMPT_BUILDER_EXAMPLE = {
  prompt: "Image prompts have their own blueprint: eight blocks, each answering a question the AI can't guess. Pick a brief, fill in the blocks (tap the suggestions to add them), and watch your prompt and frame take shape. Then copy it into ImagineArt.",
  briefs: [
    {
      id: "bakery",
      label: "Bakery Instagram post",
      icon: "🧁",
      suggestions: {
        subject: ["freshly baked cardamom buns", "a cup of chai beside warm buns", "a baker dusting sugar on pastries"],
        setting: ["a cosy Karachi bakery counter", "on a rustic wooden table", "a sunny café window"],
        style: ["realistic photo", "warm food photography", "watercolour painting"],
        composition: ["close-up", "top-down flat lay", "shallow depth of field"],
        lighting: ["soft morning window light", "golden hour", "warm café lighting"],
        mood: ["warm golden tones, inviting", "soft pastel colours, cosy"],
        text: ["no text in the image", "leave empty space at the top for a headline"],
        format: ["square 1:1", "portrait 4:5"],
      },
    },
    {
      id: "workshop",
      label: "Coding workshop banner",
      icon: "💻",
      suggestions: {
        subject: ["three students coding together on laptops", "a young woman coding on a laptop", "a mentor helping a student at a screen"],
        setting: ["a bright, modern university lab", "a co-working space with whiteboards", "a lecture hall with a big screen"],
        style: ["flat vector illustration", "3D render", "realistic photo", "minimalist"],
        composition: ["wide shot", "eye-level", "over-the-shoulder view of the screen"],
        lighting: ["bright studio lighting", "neon glow", "soft daylight"],
        mood: ["bold teal and navy, energetic", "clean blue and white, professional"],
        text: ["no text in the image", "leave empty space on the left for the workshop title"],
        format: ["wide 16:9", "vertical 9:16"],
      },
    },
    { id: "own", label: "My own idea", icon: "✏️" },
  ],
  fields: [
    { id: "subject", label: "Subject", question: "What's in the picture?", color: "indigo" as const, suggestions: ["freshly baked cardamom buns", "a young woman coding on a laptop", "a steaming cup of chai"] },
    { id: "setting", label: "Setting", question: "Where is it?", color: "emerald" as const, suggestions: ["a cosy Karachi bakery", "a bright university lab", "on a rustic wooden table"] },
    { id: "style", label: "Style", question: "What kind of image?", color: "violet" as const, suggestions: ["realistic photo", "flat vector illustration", "3D render", "watercolour painting", "minimalist"] },
    { id: "composition", label: "Composition", question: "What angle and framing?", color: "sky" as const, suggestions: ["close-up", "top-down flat lay", "wide shot", "eye-level", "shallow depth of field"] },
    { id: "lighting", label: "Lighting", question: "What light?", color: "amber" as const, suggestions: ["soft morning window light", "golden hour", "bright studio lighting", "neon glow"] },
    { id: "mood", label: "Colour and mood", question: "What feeling and palette?", color: "rose" as const, suggestions: ["warm golden tones, inviting", "bold teal and navy, energetic", "calm pastel colours"] },
    { id: "text", label: "Text", question: "Any words in the image?", color: "orange" as const, suggestions: ["no text in the image", "leave empty space at the top for a headline"] },
    { id: "format", label: "Format", question: "What shape?", color: "slate" as const, suggestions: ["square 1:1", "portrait 4:5", "vertical 9:16", "wide 16:9"], single: true },
  ],
  frame_field_id: "format",
  min_filled: 3,
  link: { label: "Open ImagineArt", url: "https://www.imagine.art" },
};
